import * as React from "react";
import { useState, useEffect, useContext } from "react";
import {
  Heading,
  HStack,
  Flex,
  Text,
  Icon,
  FormControl,
  FormLabel,
  Switch,
  Button,
  Input,
  InputGroup,
  InputRightElement,
  FormErrorMessage,
  Link,
  useColorModeValue,
  Select
} from "@chakra-ui/react";
import { FaLinkedin, FaApple } from 'react-icons/fa';
import { PageSlideFade } from "../components/page-transitions";
import { Formik, Field } from "formik";
import { AuthContext } from "../context/AuthContext";
import { apiHost, recaptchaKey } from "../utils/Cons/Constants";

import {
  GoogleReCaptchaProvider,
} from 'react-google-recaptcha-v3';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

interface RegisterFormValues {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  role: string;
  token?: string;
}

const Register = (props) => {
  const titleColor = useColorModeValue("blue.800", "blue.800");
  const textColor = useColorModeValue("gray.700", "white");
  const bgColor = useColorModeValue("white", "gray.700");
  const bgIcons = useColorModeValue("blue.800", "rgba(255, 255, 255, 0.5)");

  const [show, setShow] = useState(false);
  const handleClick = () => setShow(!show);
  const authContext = useContext(AuthContext);

  function validateEmail(value) {
    let error;
    if (!value) {
      error = "Email is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
      error = "Invalid email address";
    }
    return error;
  }

  function validateRole(value) {
    let error;
    if (!value) {
      error = "Interest is required";
    }
    return error;
  }

  function validatePassword(value) {
    let error;
    if (!value) {
      error = "Password is required";
    } else if (value.length < 6) {
      error = "Must be 6 characters or more";
    }
    return error;
  }

  function validateFirstName(value) {
    let error;
    if (!value) {
      error = "First Name is required";
    }
    return error;
  }

  function validateLastName(value) {
    let error;
    if (!value) {
      error = "Last Name is required";
    }
    return error;
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageSlideFade>
      <Flex
        direction="column"
        alignSelf="center"
        justifySelf="center"
        overflow="hidden"
      >
        <Flex alignItems="center" justifyContent="center" mb="60px" mt="20px">
          <Flex
            direction="column"
            w="445px"
            background="transparent"
            borderRadius="15px"
            p="40px"
            mx={{ base: "10px", sm: "30px" }}
            bg={bgColor}
            boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)"
          >
            <Text
              fontSize="xl"
              color={textColor}
              fontWeight="bold"
              textAlign="center"
              mb="22px"
            >
              Sign Up With
            </Text>
            <HStack spacing="15px" justify="center" mb="22px" display="none">
              <Flex
                justify="center"
                align="center"
                w="75px"
                h="75px"
                borderRadius="15px"
                border="1px solid lightgray"
                cursor="pointer"
                transition="all .25s ease"
                _hover={{ filter: "brightness(120%)", bg: bgIcons }}
              >
                <Link href="#">
                  <Icon
                    as={FaApple}
                    w="30px"
                    h="30px"
                    _hover={{ filter: "brightness(120%)" }}
                  />
                </Link>
              </Flex>
              <Flex
                justify="center"
                align="center"
                w="75px"
                h="75px"
                borderRadius="15px"
                border="1px solid lightgray"
                cursor="pointer"
                transition="all .25s ease"
                _hover={{ filter: "brightness(120%)", bg: bgIcons }}
              >
                <Link href="#">
                  <Icon
                    as={FaLinkedin}
                    w="30px"
                    h="30px"
                    _hover={{ filter: "brightness(120%)" }}
                  />
                </Link>
              </Flex>
            </HStack>
            <Text
              fontSize="lg"
              color="gray.400"
              fontWeight="bold"
              textAlign="center"
              mb="22px"
              display="none"
            >
              or
            </Text>
            <GoogleReCaptchaProvider
              reCaptchaKey={recaptchaKey}
              scriptProps={{
                async: false,
                defer: false
              }}
            >
              <Formik
                enableReinitialize
                initialValues={{ firstname: "", lastname: "", email: "", password: "", role: "Visitor" } as RegisterFormValues}
                onSubmit={(values: RegisterFormValues, actions) => {
                  console.log(values);

                  window.grecaptcha.ready(function () {
                    window.grecaptcha.execute(recaptchaKey, { action: 'submit' }).then(function (token) { 
                      values.token = token;
                      authContext.registerUser(values, props.history, true);
                    });
                  });
                  
                  actions.setSubmitting(false);
                }}
              >
                {({ values, handleChange, handleSubmit, isSubmitting }) => {
                  return (
                    <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                      <Field
                        name="role"
                        validate={validateRole}
                        width={"100%"}
                      >
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.role && form.touched.role
                            }
                            mb="24px"
                          >
                            <FormLabel htmlFor="role" ms="4px" fontSize="sm" fontWeight="normal">
                              I am a
                          </FormLabel>
                            <Select {...field}
                              id="role"
                              borderRadius="15px"
                              fontSize="sm"
                              size="lg"
                              value={values.role}
                              onChange={handleChange}
                            >
                              <option value="Visitor">Bellefonte Visitor</option>
                              <option value="Local">Bellefonte Local</option>
                              <option value="Destination">Bellefonte Destination</option>
                            </Select>
                            <FormErrorMessage>
                              {form.errors.role}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <Field
                        name="firstname"
                        validate={validateFirstName}
                        width={"100%"}
                      >
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.firstname && form.touched.firstname
                            }
                            mb="24px"
                          >
                            <FormLabel htmlFor="firstname" ms="4px" fontSize="sm" fontWeight="normal">
                              First Name
                          </FormLabel>
                            <Input
                              {...field}
                              id="firstname"
                              borderRadius="15px"
                              fontSize="sm"
                              type="text"
                              placeholder="Your first name"
                              size="lg"
                              value={values.firstname}
                              onChange={handleChange}
                            />
                            <FormErrorMessage>
                              {form.errors.firstname}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <Field
                        name="lastname"
                        validate={validateLastName}
                        width={"100%"}
                      >
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.lastname && form.touched.lastname
                            }
                            mb="24px"
                          >
                            <FormLabel htmlFor="lastname" ms="4px" fontSize="sm" fontWeight="normal">
                              Last Name
                          </FormLabel>
                            <Input
                              {...field}
                              id="lastname"
                              borderRadius="15px"
                              fontSize="sm"
                              type="text"
                              placeholder="Your last name"
                              size="lg"
                              value={values.lastname}
                              onChange={handleChange}
                            />
                            <FormErrorMessage>
                              {form.errors.lastname}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <Field
                        name="email"
                        validate={validateEmail}
                        width={"100%"}
                      >
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.email && form.touched.email
                            }
                            mb="24px"
                          >
                            <FormLabel htmlFor="email" ms="4px" fontSize="sm" fontWeight="normal">
                              Email
                          </FormLabel>
                            <Input
                              {...field}
                              id="email"
                              borderRadius="15px"
                              fontSize="sm"
                              type="text"
                              placeholder="Your email address"
                              size="lg"
                              value={values.email}
                              onChange={handleChange}
                            />
                            <FormErrorMessage>
                              {form.errors.email}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <Field
                        name="password"
                        validate={validatePassword}
                        width={"100%"}
                      >
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.password && form.touched.password
                            }
                          >
                            <FormLabel htmlFor="password" ms="4px" fontSize="sm" fontWeight="normal">
                              Password
                          </FormLabel>
                            <InputGroup size="md">
                              <Input
                                {...field}
                                id="password"
                                pr="4.5rem"
                                borderRadius="15px"
                                fontSize="sm"
                                size="lg"
                                type={show ? "text" : "password"}
                                placeholder="Your password"
                                value={values.password}
                                onChange={handleChange}
                              />
                              <InputRightElement width="4.5rem">
                                <Button
                                  h="2.5rem"
                                  mt="0.5rem"
                                  size="sm"
                                  onClick={handleClick}
                                >
                                  {show ? "Hide" : "Show"}
                                </Button>
                              </InputRightElement>
                            </InputGroup>
                            <FormErrorMessage>
                              {form.errors.password}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <Button
                        fontSize="10px"
                        type="submit"
                        bg="blue.700"
                        w="100%"
                        h="45"
                        mb="20px"
                        color="white"
                        mt="20px"
                        _hover={{
                          bg: "blue.400",
                        }}
                        _active={{
                          bg: "blue.500",
                        }}
                        isLoading={isSubmitting}
                      >
                        SIGN UP
                    </Button>
                    </form>
                  );
                }}
              </Formik>
            </GoogleReCaptchaProvider>
            <Flex
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              maxW="100%"
              mt="0px"
            >
              <Text color={textColor} fontWeight="medium">
                Already have an account?
                <Link
                  color={titleColor}
                  ms="5px"
                  href={'/login'}
                  fontWeight="bold"
                >
                  Sign In
                </Link>
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </PageSlideFade>
  );
};

export default Register;
